/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import error from '../../../../common/nodejs/errors';
import { mapState, mapActions } from 'vuex';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';

/** 各フェーズ定義 */
const PHASE = {
  CONFIRM: 'confirm',
  EXECUTE: 'execute',
  SUCCESS: 'success',
  ERROR: 'error',
  TIMEOUT: 'timeout',
  DTC_ERROR: 'dtc_error',
  EXECUTE_ERROR: 'execute_error'
};

/** API type定義 */
const API_TYPE = {
  STANDBY: 'standby',
  READ_DTC: 'read_dtc',
  EXECUTE: 'execute',
  TERMINATE: 'terminate',
  DISPOSE: 'dispose'
};

/** 現在セッション */
const SESSION_PROPERTY = {
  DEFAULT: 0,
  NON_DEFAULT: 1
};

export default {
  name: 'requirement50018',

  data() {
    return {
      phase: PHASE.CONFIRM,
      isLoading: false,
      // ダイアログフラグ
      modal_requirement_50018: false,
      sessionProperty: SESSION_PROPERTY.DEFAULT,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR:
        case PHASE.TIMEOUT:
        case PHASE.DTC_ERROR:
        case PHASE.EXECUTE_ERROR:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.CONFIRM:
        case PHASE.EXECUTE:
        default:
          return 'secondary';
      }
    },

    /**
     * 表示ヘッダー文字列判定
     * @returns {string} ヘッダー文字列
     */
    headerTitle() {
      const ConfirmTitle = 'message.common_title_confirmation';
      const ExecuteTitle = 'individual.common_title_execution';
      const SuccessTitle = 'message.common_title_success';
      const ErrorTitle = 'message.common_title_error';

      const titleMap = {
        [PHASE.CONFIRM]: ConfirmTitle,
        [PHASE.EXECUTE]: ExecuteTitle,
        [PHASE.SUCCESS]: SuccessTitle,
        [PHASE.DTC_ERROR]: ErrorTitle,
        [PHASE.TIMEOUT]: ErrorTitle,
        [PHASE.EXECUTE_ERROR]: ErrorTitle,
        [PHASE.ERROR]: ErrorTitle,
      };

      return this.$t(titleMap[this.phase]);
    },

    /**
     * 作業サポート表示メッセージ判定
     * @returns {string} メッセージ
     */
    workSupportMessage() {
      const messageMap = {
        [PHASE.CONFIRM]: `individual.label_battery_status_info_update_start`,
        [PHASE.EXECUTE]: `individual.label_wait`,
        [PHASE.SUCCESS]: `individual.label_battery_status_info_update_end`,
        [PHASE.DTC_ERROR]: `individual.label_battery_status_info_update_dtc_stored`,
        [PHASE.TIMEOUT]: `individual.label_timed_out`,
        [PHASE.EXECUTE_ERROR]: `individual.label_battery_status_info_update_failed`,
        [PHASE.ERROR]: `individual.error_communication_error`,
      };

      return this.$t(messageMap[this.phase]);
    },

    /**
     * [OK]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonOk() {
      const showCancelButtonMap = {
        [PHASE.CONFIRM]: true,
        [PHASE.EXECUTE]: false,
        [PHASE.SUCCESS]: true,
        [PHASE.DTC_ERROR]: true,
        [PHASE.TIMEOUT]: true,
        [PHASE.EXECUTE_ERROR]: true,
        [PHASE.ERROR]: true,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * [キャンセル]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.CONFIRM]: true,
        [PHASE.EXECUTE]: false,
        [PHASE.SUCCESS]: false,
        [PHASE.DTC_ERROR]: false,
        [PHASE.TIMEOUT]: false,
        [PHASE.EXECUTE_ERROR]: false,
        [PHASE.ERROR]: false,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.sessionProperty = SESSION_PROPERTY.DEFAULT;
      this.modal_requirement_50018 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_50018 = false;
    },

    /**
     * 共通の[キャンセル]ボタンクリック処理
     */
    onCancelClicked() {
      switch (this.phase) {
        case PHASE.CONFIRM:
          this.clickStartCancel();
          break;
        default:
          // noop
          break;
      }
    },

    /**
     * 共通の[OK]ボタンクリック処理
     */
    onOkClicked() {
      switch (this.phase) {
        case PHASE.CONFIRM:
          this.clickStartOk();
          break;
        case PHASE.SUCCESS:
          this.clickSucceedOk();
          break;
        case PHASE.ERROR:
        case PHASE.TIMEOUT:
        case PHASE.DTC_ERROR:
        case PHASE.EXECUTE_ERROR:
          this.clickErrorOk();
          break;
        default:
          // noop
          break;
      }
    },

    /**
     * 実行確認画面のキャンセルクリック
     */
    clickStartCancel() {
      this.closeWizard();
    },

    /**
     * 実行確認画面のOKクリック
     */
    async clickStartOk() {
      // 実行前確認処理を実行
      this.phase = PHASE.EXECUTE;
      this.confirmExecution();
    },

    /**
     * 成功画面のOKクリック
     */
    async clickSucceedOk() {
      await this.terminate();
    },

    /**
     * エラー画面のOKクリック
     */
    async clickErrorOk() {
      if (this.sessionProperty === SESSION_PROPERTY.NON_DEFAULT) {
        // NonDefaultSession遷移済み
        await this.terminate();
      } else {
        await this.exit();
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 
     * API 実行処理
     * @param {string} type API実行種別
     * @returns {object?} API実行結果
     */
    async executeApi(type) {
      const systemId = individualDiagnose.getCurrentSystemId(this);
      const token = session.getToken();
      // ポーリング
      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: type
        },
        // エラー発生のコールバックを実装する。
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列、もしくは独自定義した文字列が返ってくる。
        // 必要に応じて、errorType に応じてメッセージを変えるといったことも可能。
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );
      return response;
    },

    /** NonDefaultSession遷移 */
    async confirmExecution() {
      try {
        this.isLoading = true;
        const response = await this.executeApi(API_TYPE.STANDBY);
        // responseが存在しない場合、共通エラーに遷移
        if (!response) {
          this.showUnexpectedError();
          return;
        }

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // NonDefaultSession遷移成功
            this.sessionProperty = SESSION_PROPERTY.NON_DEFAULT;
            // 正常終了 DTC読出し処理
            await this.readDtc();
            break;
          case REQUEST_STATUS_FAILED:
          default:
            this.showUnexpectedError();
            break;
        }

      } finally {
        this.isLoading = false;
      }
    },

    /** DTC読出し */
    async readDtc() {
      try {
        this.isLoading = true;
        const response = await this.executeApi(API_TYPE.READ_DTC);
        // responseが存在しない場合、共通エラーに遷移
        if (!response) {
          this.showUnexpectedError();
          return;
        }

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了：成功表示
            await this.execute();
            break;
          case REQUEST_STATUS_FAILED:
            // 異常終了
            if (response.data.async_code === error.WSP_50018_DTC_ERROR) {
              // DTC有り
              this.phase = PHASE.DTC_ERROR;
            } else {
              this.showUnexpectedError();
            }
            break;
          default:
            this.showUnexpectedError();
            break;
        }

      } finally {
        this.isLoading = false;
      }
    },

    /** 実行処理 */
    async execute() {
      try {
        this.isLoading = true;
        const response = await this.executeApi(API_TYPE.EXECUTE);
        // responseが存在しない場合、共通エラーに遷移
        if (!response) {
          this.showUnexpectedError();
          return;
        }

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了：成功表示
            this.phase = PHASE.SUCCESS;
            break;
          case REQUEST_STATUS_FAILED:
            // 異常終了
            if (response.data.async_code === error.WSP_50018_TIMEOUT_ERROR) {
              // RoutineControlの実行値が(SID0x2F/0x31)実行監視_継続条件と一致した時間が30分を超えた
              this.phase = PHASE.TIMEOUT;
            } else if (response.data.async_code === error.WSP_50018_EXECUTE_ERROR) {
              // RoutineControlの実行値が(SID0x2F/0x31)が終了条件とも継続条件とも一致しない
              this.phase = PHASE.EXECUTE_ERROR;
            } else {
              this.showUnexpectedError();
            }
            break;
          default:
            this.showUnexpectedError();
            break;
        }

      } finally {
        this.isLoading = false;
      }

    },

    /** API_TYPE: terminateの実行 */
    async terminate() {
      try {
        this.isLoading = true;
        // 実行後の処理分岐のために実行時のフェーズを保持
        const currentPhase = this.phase;
        const response = await this.executeApi(API_TYPE.TERMINATE);

        if (currentPhase === PHASE.ERROR) {
          // 実行時にエラー画面だった場合は機能終了
          await this.exit();
          return;
        }

        if (!response) {
          this.showUnexpectedError();
          return;
        }

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            // 機能終了
            await this.exit();
            break;
          case REQUEST_STATUS_FAILED:
          default:
            this.showUnexpectedError();
            break;
        }

      } finally {
        this.isLoading = false;
      }
    },

    /** 
     * API_TYPE: disposeの実行 
     * エラー処理が異なるため独自実装
     */
    async dispose() {
      try {
        this.isLoading = true;
        await this.executeApi(API_TYPE.DISPOSE);
      } finally {
        this.isLoading = false;
      }
    },

    /** 機能終了処理 */
    async exit() {
      await this.dispose();
      this.closeWizard();
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      this.isLoading = false;
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },
  },

  async mounted() {
    this.init();
  },
};